import { motion, useReducedMotion } from 'framer-motion';
import { Check, Sparkles } from 'lucide-react';

import { cn } from '@/lib/utils';

export type ArdiOrbState = 'idle' | 'thinking' | 'done';

/**
 * Ardi, as a presence rather than a mascot.
 *
 * The same glyph Ardi wears everywhere else (Sparkles, grey on white, in a
 * hairline circle), given a pulse: idle breathes slowly, thinking runs an ink
 * arc round the rim, done settles into a check. Motion says what Ardi is
 * doing; there is no face and no colour to learn.
 */
export function ArdiOrb({ state = 'idle', size = 56, className }: { state?: ArdiOrbState; size?: number; className?: string }) {
  const reduce = useReducedMotion();
  const r = size / 2 - 1.5;
  const c = 2 * Math.PI * r;
  const glyph = Math.round(size * 0.38);

  return (
    <span className={cn('relative inline-flex shrink-0 items-center justify-center', className)} style={{ width: size, height: size }} aria-hidden>
      {/* The halo: a soft ring that breathes out and fades. */}
      {!reduce && state !== 'done' && (
        <motion.span
          className="absolute inset-0 rounded-full border border-foreground/15"
          initial={{ scale: 1, opacity: 0.6 }}
          animate={{ scale: state === 'thinking' ? 1.45 : 1.25, opacity: 0 }}
          transition={{ duration: state === 'thinking' ? 1.6 : 2.8, repeat: Infinity, ease: 'easeOut' }}
        />
      )}
      <motion.span
        className="relative flex size-full items-center justify-center rounded-full border bg-background"
        animate={reduce ? undefined : { scale: state === 'idle' ? [1, 1.03, 1] : 1 }}
        transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
      >
        {state === 'done' ? (
          <motion.span initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 380, damping: 22 }}>
            <Check className="text-foreground" style={{ width: glyph, height: glyph }} strokeWidth={2.25} />
          </motion.span>
        ) : (
          <Sparkles className="text-muted-foreground" style={{ width: glyph, height: glyph }} strokeWidth={1.75} />
        )}
      </motion.span>
      {/* The working arc, over the rim. */}
      {state === 'thinking' && (
        <motion.svg
          className="absolute inset-0"
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          animate={reduce ? undefined : { rotate: 360 }}
          transition={{ duration: 1.4, repeat: Infinity, ease: 'linear' }}
        >
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--foreground)" strokeWidth={1.5} strokeLinecap="round" strokeDasharray={`${c * 0.22} ${c}`} />
        </motion.svg>
      )}
    </span>
  );
}
